import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bookAppointment, getAvailableSlots, rescheduleAppointment } from '../../redux/appointmentSlice';
import { hasControl, SECURED_VIDEO_MSG } from '../../redux/subscriptionSlice';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { currencySymbol, formatPrice } from '../../utils/currency';
import PaymentMethodSelect from '../payment/PaymentMethodSelect';
import { formatTime as formatClockTime } from '../../utils/clock';

// Read-only display option that looks up a specialty name from the master cache by ID.
// Used inside the disabled <select> that previews the currently-selected specialty.
const DisabledSpecialityOption = ({ name, doctor }) => {
    const specialtyId = doctor?.appointmentSpeciality?.find((s) => s.name === name)?.specialtyId;
    const label = useMasterLabel('masterSpecialty', specialtyId, 'name', name || '');
    return <option value={name}>{label}</option>;
};


const PatientAppointmentModal = ({
  isModalOpen,
  setIsModalOpen,
  selectedSlot,
  setSelectedSlot,
  selectedDoctor,
  selectedPatient,
  selectedSpeciality,
  selectedAddressId,
  selectedAppointmentType,
  startDate: propStartDate,
  endDate: propEndDate,
  isRescheduling,
  onRescheduleConfirm,
  rescheduleData,
  selectedCustomPrice,
  onAppointmentBooked,
}) => {
  const { t, i18n } = useTranslation('patientappointmentmodal');
  const dispatch = useDispatch();
  const { isLoading: appointmentLoading, error: appointmentError } = useSelector((state) => state.appointment);

  const [doctor, setDoctor] = useState('');
  const [location, setLocation] = useState('');
  const [speciality, setSpeciality] = useState('');
  const [appointmentType, setAppointmentType] = useState('');
  const [duration, setDuration] = useState('');
  const [price, setPrice] = useState(0);
  const [mode, setMode] = useState('visio');
  const [communication, setCommunication] = useState('');
  const [communicationError, setCommunicationError] = useState('');
  const [payment, setPayment] = useState({ method: '', timing: 'postpaid' });

  const parseDuration = (duration) => {
    if (!duration) return 60;
    if (typeof duration === 'number') return duration;
    const [hours, minutes] = duration.split(':').map(Number);
    return hours * 60 + (minutes || 0);
  };

  const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}:${mins.toString().padStart(2, '0')}`;
  };

  // "MM/DD/YYYY, 2:30 PM" — DISPLAY only; the payload uses formatTimeForPayload
  // (24-hour). One clock format (owner, 15 Oct 2026).
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    const day = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    return `${day}, ${formatClockTime(date)}`;
  };

  const formatTimeForPayload = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const getDayOfWeek = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const docTz = selectedDoctor?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return date.toLocaleString('en-US', { weekday: 'long', timeZone: docTz });
  };
  const clickedDay = selectedSlot ? getDayOfWeek(selectedSlot.start) : '';

  const dayAvailability = selectedDoctor?.availability?.find(
    (avail) => avail.day === clickedDay
  );

  // Determine addressTypeId: 
  // 1. If dayAvailability exists, use its addressType
  // 2. Otherwise, if selectedAddressId is provided, find the address by ID and get its addressType
  // 3. Fall back to selectedAddressId if it's already an addressType string
  let addressTypeId = dayAvailability?.addressType;
  if (!addressTypeId && selectedAddressId) {
    // Try to find address by ID first (for All Doctor mode)
    const addressById = selectedDoctor?.address?.find(
      (addr) => addr.addressId === selectedAddressId
    );
    if (addressById) {
      addressTypeId = addressById.addressType;
    } else {
      // If not found by ID, assume selectedAddressId is already an addressType string
      addressTypeId = selectedAddressId;
    }
  }

  const selectedAddress = selectedDoctor?.address?.find(
    (addr) => addr.addressId === addressTypeId || addr.addressType === addressTypeId
  );
  const locations = selectedAddress ? [selectedAddress.addressType] : [t('patientappointmentmodal.no_address_available')];
  const country = selectedAddress?.country || t('patientappointmentmodal.unknown');

  const specialities = selectedAddress?.specialty?.map((s) => s.name) || [];

  const selectedSpecialityData = selectedDoctor?.appointmentSpeciality?.find(
    (s) => s.name === selectedSpeciality
  );

  const appointmentTypes = useMemo(() => {
    return selectedSpecialityData?.appointmentTypes?.map((appt) => ({
      type: appt.type,
      duration: parseDuration(appt.duration),
      price: parseInt(appt.price, 10),
      specialtyId: appt.specialtyId,
      virtual: appt.virtual,
      appointmentTypeId: appt.typeId,
    })) || [];
  }, [selectedSpecialityData]);

  const selectedApptType = appointmentTypes.find((appt) => appt.type === appointmentType);
  const isVirtualAllowed = selectedApptType?.virtual || false;
  // Video only if the SELECTED doctor's plan includes secured_video (fail-open
  // when planControls hasn't loaded). Combined with isVirtualAllowed below.
  const videoAllowed = selectedDoctor?.planControls == null
    ? true
    : hasControl(selectedDoctor.planControls, 'secured_video');

  useEffect(() => {
    if (isModalOpen && selectedSpeciality && selectedAppointmentType && selectedAddressId) {
      // Set doctor name
      const doctorName = selectedDoctor?.name ||
        (selectedDoctor?.firstName && selectedDoctor?.lastName
          ? `${selectedDoctor.firstName} ${selectedDoctor.lastName}`
          : '') ||
        t('patientappointmentmodal.doctor_fallback');
      setDoctor(doctorName);
      setLocation(locations[0] || t('patientappointmentmodal.no_address_available'));
      setSpeciality(selectedSpeciality);

      const apptType = appointmentTypes.find((appt) => appt.type === selectedAppointmentType);
      if (apptType) {
        setAppointmentType(selectedAppointmentType);
        setDuration(apptType.duration);
        // Use custom price if available, otherwise use normal price
        if (selectedCustomPrice?.customPrice !== undefined && selectedCustomPrice?.customPrice !== null) {
          setPrice(selectedCustomPrice.customPrice || 0);
        } else {
          setPrice(apptType.price || 0);
        }
        setMode(isRescheduling ? (rescheduleData?.appointmentMode === 'visio' ? 'visio' : 'onsite') : apptType.virtual ? 'visio' : 'onsite');
      }

      if (isRescheduling && rescheduleData) {
        setCommunication(rescheduleData.description || '');
      }
    }
  }, [isModalOpen, selectedSpeciality, selectedAppointmentType, selectedAddressId, selectedDoctor, appointmentTypes, isRescheduling, rescheduleData, selectedCustomPrice]);

  // Update price when custom price changes
  useEffect(() => {
    if (isModalOpen && selectedAppointmentType) {
      const apptType = appointmentTypes.find((appt) => appt.type === selectedAppointmentType);
      if (apptType) {
        // Use custom price if available, otherwise use normal price
        if (selectedCustomPrice?.customPrice !== undefined && selectedCustomPrice?.customPrice !== null) {
          setPrice(selectedCustomPrice.customPrice || 0);
        } else {
          // Use normal price from appointment type
          setPrice(apptType.price || 0);
        }
      }
    }
  }, [selectedCustomPrice, isModalOpen, selectedAppointmentType, appointmentTypes]);

  const computedEndTime = () => {
    if (selectedSlot && selectedAppointmentType) {
      const apptType = appointmentTypes.find((appt) => appt.type === selectedAppointmentType);
      if (apptType && selectedSlot.start) {
        const start = new Date(selectedSlot.start);
        const end = new Date(start.getTime() + apptType.duration * 60 * 1000);
        return end.toISOString();
      }
    }
    return selectedSlot?.end || '';
  };

  // Validate communication text
  const validateCommunicationText = (value) => {
    if (!value) return '';

    // Sanitize input - remove dangerous characters
    const sanitized = value.replace(/[<>]/g, '');

    // Check max length
    if (sanitized.length > 500) {
      return t('patientappointmentmodal.communication_max_length');
    }

    // Check for only whitespace
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
      return t('patientappointmentmodal.communication_only_whitespace');
    }

    // Check for more than 2 consecutive special characters
    if (/[!@#$%^&*(),.?":{}|<>]{3,}/.test(sanitized)) {
      return t('patientappointmentmodal.communication_consecutive_special');
    }

    return '';
  };

  const handleCommunicationChange = (e) => {
    const value = e.target.value;

    // Clear error if user is typing
    if (communicationError && value.length < communication.length) {
      setCommunicationError('');
    }

    // Validate input
    const error = validateCommunicationText(value);
    if (error) {
      setCommunicationError(error);
    } else {
      // Sanitize input - remove dangerous characters
      const sanitized = value.replace(/[<>]/g, '');
      // Enforce max length
      const finalValue = sanitized.length > 500
        ? sanitized.substring(0, 500)
        : sanitized;
      setCommunication(finalValue);
      setCommunicationError('');
    }
  };

  const handleSaveAppointment = async (e) => {
    e.preventDefault();

    // Validate communication
    const communicationValidationError = validateCommunicationText(communication);
    if (communicationValidationError) {
      setCommunicationError(communicationValidationError);
      toast.warn(communicationValidationError);
      return;
    }

    if (!doctor.trim()) {
      toast.warn(t('patientappointmentmodal.please_select_doctor'));
      return;
    }
    if (!speciality) {
      toast.warn(t('patientappointmentmodal.validation_speciality_error'));
      return;
    }
    if (!appointmentType) {
      toast.warn(t('patientappointmentmodal.validation_appointment_type_error'));
      return;
    }

    if (!selectedDoctor) {
      toast.warn(t('patientappointmentmodal.please_select_doctor'));
      return;
    }

    const selectedPatientObj = selectedPatient;
    if (!selectedPatientObj) {
      toast.warn(t('patientappointmentmodal.validation_invalid_patient_error'));
      return;
    }

    const doctorId = selectedDoctor.doctorId;
    const patientId = selectedPatientObj.patientId;
    const selectedApptType = appointmentTypes.find((appt) => appt.type === appointmentType);
    if (!selectedApptType) {
      toast.warn(t('patientappointmentmodal.validation_invalid_appointment_type_error'));
      return;
    }

    // Validate payment (only on new bookings — matches when PaymentMethodSelect shows)
    if (!isRescheduling) {
      // `unavailable` means /payment/options ANSWERED with an empty list (hospital
      // switch off, doctor accepts none, platform hold, plan without the feature).
      // Never book past that — it would record a payment nobody agreed to.
      if (payment.unavailable) {
        toast.warn(t('patientappointmentmodal.validation_no_payment_method_accepted', 'This doctor accepts no payment method yet — set one up before booking.'));
        return;
      }
      if (!payment.method) {
        toast.warn(t('patientappointmentmodal.validation_payment_method_error', 'Please choose a payment method'));
        return;
      }
      if (payment.timing === 'prepaid' && payment.method === 'cash') {
        toast.warn(t('patientappointmentmodal.validation_prepaid_not_cash_error', 'Cash is not available for advance (online) payment'));
        return;
      }
    }

    const startDate = new Date(selectedSlot.start);
    const date = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
    const time = formatTimeForPayload(selectedSlot.start);
    const slotEndTime = formatTimeForPayload(computedEndTime());
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const appointmentMode = mode === 'visio' ? 'visio' : 'onsite';
    const formattedDuration = formatDuration(duration);

    // Determine the price to use in payload:
    // 1. If custom price exists and is available, use it
    // 2. Otherwise, use the price from state (which could be normal price)
    // 3. If price is empty, use default from appointment type
    let finalPrice;
    if (selectedCustomPrice?.customPrice !== undefined && selectedCustomPrice?.customPrice !== null) {
      // Use custom price if available
      finalPrice = typeof selectedCustomPrice.customPrice === 'number'
        ? selectedCustomPrice.customPrice
        : parseFloat(selectedCustomPrice.customPrice) || 0;
    } else {
      // Use price from state (normal price)
      if (price === '' || price === null || price === undefined) {
        // If price is empty, use default from appointment type
        finalPrice = selectedApptType?.price || 0;
      } else if (typeof price === 'string') {
        const parsed = parseFloat(price);
        finalPrice = isNaN(parsed) ? (selectedApptType?.price || 0) : parsed;
      } else if (typeof price === 'number') {
        finalPrice = price;
      } else {
        finalPrice = selectedApptType?.price || 0;
      }
    }

    // Determine pricetype based on the price source:
    // Type 1: Price comes from appointmenttype (default, not from custom price API)
    // Type 2: Price comes from customprice API response
    // Note: PatientAppointmentModal doesn't allow manual price editing, so Type 3 is not applicable
    let priceType = 'Type 1'; // Default to appointment type price
    if (selectedCustomPrice?.customPrice !== undefined && selectedCustomPrice?.customPrice !== null) {
      const customPriceValue = typeof selectedCustomPrice.customPrice === 'number'
        ? selectedCustomPrice.customPrice
        : parseFloat(selectedCustomPrice.customPrice) || 0;

      // If final price matches custom price from API, it's Type 2
      if (finalPrice === customPriceValue) {
        priceType = 'Type 2'; // Using custom price from API
      } else {
        // This shouldn't happen in PatientAppointmentModal since price is read-only,
        // but fallback to Type 1 if prices don't match
        priceType = 'Type 1';
      }
    } else {
      priceType = 'Type 1'; // Using appointment type price (default)
    }

    const payload = {
      patientId,
      doctorId,
      date,
      time,
      slotEndTime,
      timeZone,
      appointmentTypeId: selectedApptType.appointmentTypeId,
      description: communication || '',
      appointmentMode,
      SpecialtyId: selectedApptType.specialtyId,
      addressType: addressTypeId,
      duration: formattedDuration,
      price: finalPrice,
      pricetype: priceType,
      ...(payment.method && !isRescheduling
        ? { paymentMethod: payment.method, paymentTiming: payment.timing }
        : {}),
    };

    if (isRescheduling) {
      const result = await Swal.fire({
        title: t('patientappointmentmodal.confirm_reschedule_title'),
        text: t('patientappointmentmodal.confirm_reschedule_text', { patient: doctor, time: formatTime(selectedSlot.start) }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4ABDBD',
        cancelButtonColor: '#e5e7eb',
        confirmButtonText: t('patientappointmentmodal.reschedule_button', 'Reschedule'),
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        const reschedulePayload = {
          ...payload,
          appointmentId: rescheduleData.appointmentId,
          newDate: payload.date,
          newTime: payload.time,
          newSlotEndTime: payload.slotEndTime,
        };

        const actionResult = await dispatch(rescheduleAppointment(reschedulePayload));

        if (actionResult.meta.requestStatus === 'fulfilled') {
          let startDate = propStartDate;
          let endDate = propEndDate;
          if (!startDate || !endDate) {
            const selectedDate = new Date(selectedSlot.start);
            const dayOfWeek = selectedDate.getDay();
            const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            startDate = new Date(selectedDate);
            startDate.setDate(selectedDate.getDate() - diffToMonday);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 6);
            endDate.setHours(23, 59, 59, 999);
          }

          await dispatch(getAvailableSlots({
            doctorId: selectedDoctor?.doctorId,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            timeZone: timeZone,
            addressType: selectedAddressId,
            appointmentTypeId: selectedDoctor?.appointmentSpeciality.find(
              spec => spec.name === selectedSpeciality
            )?.appointmentTypes.find(type => type.type === selectedAppointmentType)?.typeId,
            duration: typeof duration === 'number' ? duration : parseDuration(duration),
            isReschedulingProcess: true,
          }));

          onRescheduleConfirm(reschedulePayload);
          handleCloseModal();
          toast.success(t('patientappointmentmodal.reschedule_success_text'));
          // Call the callback to close parent modal and refresh library
          if (onAppointmentBooked) {
            onAppointmentBooked();
          }
        } else {
          throw new Error(extractErrorMessage(actionResult.payload, t('patientappointmentmodal.reschedule_error_text'), i18n.language));
        }
      } catch (err) {
        toast.warn(extractErrorMessage(err, t('patientappointmentmodal.reschedule_error_text'), i18n.language));
      }
    } else {
      const result = await Swal.fire({
        title: t('patientappointmentmodal.confirm_create_title'),
        text: t('patientappointmentmodal.confirm_create_text', { patient: doctor, time: formatTime(selectedSlot.start) }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4ABDBD',
        cancelButtonColor: '#e5e7eb',
        confirmButtonText: t('patientappointmentmodal.create_button', 'Create'),
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        const actionResult = await dispatch(bookAppointment(payload));

        if (actionResult.meta.requestStatus === 'fulfilled') {
          let startDate = propStartDate;
          let endDate = propEndDate;
          if (!startDate || !endDate) {
            const selectedDate = new Date(selectedSlot.start);
            const dayOfWeek = selectedDate.getDay();
            const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            startDate = new Date(selectedDate);
            startDate.setDate(selectedDate.getDate() - diffToMonday);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 6);
            endDate.setHours(23, 59, 59, 999);
          }

          await dispatch(getAvailableSlots({
            doctorId: selectedDoctor?.doctorId,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            timeZone: timeZone,
            addressType: selectedAddressId,
            appointmentTypeId: selectedDoctor?.appointmentSpeciality.find(
              spec => spec.name === selectedSpeciality
            )?.appointmentTypes.find(type => type.type === selectedAppointmentType)?.typeId,
            duration: typeof duration === 'number' ? duration : parseDuration(duration),
            isReschedulingProcess: true,
          }));

          handleCloseModal();
          toast.success(t('patientappointmentmodal.create_success_text'));
          // Call the callback to close parent modal and refresh library
          if (onAppointmentBooked) {
            onAppointmentBooked();
          }
        } else {
          throw new Error(extractErrorMessage(actionResult.payload, t('patientappointmentmodal.error_text'), i18n.language));
        }
      } catch (err) {
        toast.warn(extractErrorMessage(err, t('patientappointmentmodal.error_text'), i18n.language));
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setDoctor('');
    setLocation('');
    setSpeciality('');
    setAppointmentType('');
    setDuration('');
    setPrice(0);
    setMode('visio');
    setCommunication('');
    setCommunicationError('');
    setPayment({ method: '', timing: 'postpaid' });
    setSelectedSlot(null);
  };

  if (!isModalOpen) return null;

  return (
    <div data-testid="patient-appointment-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div data-testid="patient-appointment-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
        {/* Header Section - Sticky */}
        <div data-testid="patient-appointment-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
          <h3 data-testid="patient-appointment-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
            {isRescheduling ? t('patientappointmentmodal.reschedule_appointment') : t('patientappointmentmodal.add_appointment')}
          </h3>
          <button
            data-testid="close-patient-appointment-modal"
            id="btn-close-modal"
            onClick={handleCloseModal}
            className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
          >
            <X data-testid="close-modal-icon" className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
          {/* The store keeps a refusal as an OBJECT ({ message: {en, fr}, status,
              data, code }); rendered as a child it threw and took the booking
              screen down (P26). Always read out as the sentence. */}
          {appointmentError && (
            <div data-testid="appointment-error" role="alert" className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-xl mb-4 shadow-md">
              {extractErrorMessage(appointmentError, t('patientappointmentmodal.error_text'), i18n.language)}
            </div>
          )}
          <form
            data-testid="patient-appointment-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveAppointment(e);
            }}
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${appointmentLoading ? "opacity-50 pointer-events-none" : ""}`}
          >
            {/* <div data-testid="basic-information-section" className="sm:col-span-2 lg:col-span-3">
              <h4 data-testid="basic-information-title" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-4">
                {t('patientappointmentmodal.basic_information')}
              </h4>
            </div> */}
            <div data-testid="doctor-field-container">
              <label htmlFor="doctor" data-testid="doctor-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.doctor_label') || 'Doctor'}
              </label>
              <select
                id="doctor"
                data-testid="doctor-select"
                value={doctor}
                className="tt-select w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
                disabled
              >
                <option value={doctor}>{doctor}</option>
              </select>
            </div>
            <div data-testid="speciality-field-container">
              <label htmlFor="speciality" data-testid="speciality-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.speciality_label')}
              </label>
              <select
                id="speciality"
                data-testid="speciality-select"
                value={speciality}
                className="tt-select w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
                disabled
              >
                <DisabledSpecialityOption name={speciality} doctor={selectedDoctor} />
              </select>
            </div>
            <div data-testid="appointment-type-field-container">
              <label htmlFor="appointmentType" data-testid="appointment-type-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.appointment_type_label')}
              </label>
              <select
                id="appointmentType"
                data-testid="appointment-type-select"
                value={appointmentType}
                className="tt-select w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
                disabled
              >
                <option value={appointmentType}>{appointmentType}</option>
              </select>
            </div>
            <div data-testid="mode-field-container" className="col-span-1">
              <label htmlFor="mode" data-testid="mode-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.appointment_mode_label')}
              </label>
              <div className="flex items-center justify-between sm:justify-start gap-2">
                <span className="text-sm text-gray-700">{t('patientappointmentmodal.onsite')}</span>
                <label title={!videoAllowed ? SECURED_VIDEO_MSG : ''} className={`relative inline-flex items-center ${!videoAllowed ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    id="mode"
                    data-testid="mode-toggle"
                    checked={mode === 'visio'}
                    onChange={() => setMode(mode === 'visio' ? 'onsite' : 'visio')}
                    className="sr-only peer"
                    disabled={appointmentLoading || !isVirtualAllowed || !videoAllowed}
                  />
                  <div
                    className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-[#4ABDBD] transition-all ${!isVirtualAllowed || appointmentLoading || !videoAllowed ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <div
                      className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${mode === 'visio' ? "translate-x-5" : "translate-x-0"}`}
                    ></div>
                  </div>
                </label>
                <span className="text-sm text-gray-700">{t('patientappointmentmodal.online')}</span>
              </div>
            </div>
            <div data-testid="duration-field-container">
              <label htmlFor="duration" data-testid="duration-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.duration_label')}
              </label>
              <input
                id="duration"
                data-testid="duration-input"
                type="text"
                value={duration ? formatDuration(duration) : ''}
                readOnly
                className="w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
              />
            </div>
            <div data-testid="price-field-container">
              <label htmlFor="price" data-testid="price-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.price_label', 'Price')} {currencySymbol(selectedDoctor?.country) ? `(${currencySymbol(selectedDoctor?.country)})` : ''}
              </label>
              <div className="relative">
                <input
                  id="price"
                  data-testid="price-input"
                  type="number"
                  value={price ?? 0}
                  readOnly
                  className="w-full p-3 pl-10 rounded-xl border border-gray-300 bg-gray-100 text-sm"
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-semibold">
                  {currencySymbol(selectedDoctor?.country)}
                </span>
              </div>
            </div>
            <div data-testid="location-field-container">
              <label htmlFor="location" data-testid="location-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.location_label')}
              </label>
              <select
                id="location"
                data-testid="location-input"
                value={location}
                className="tt-select w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
                disabled
              >
                <option value={location}>{location}</option>
              </select>
            </div>
            <div data-testid="start-time-field-container">
              <label htmlFor="startTime" data-testid="start-time-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.start_time_label')}
              </label>
              <input
                id="startTime"
                data-testid="start-time-input"
                type="text"
                value={formatTime(selectedSlot?.start)}
                readOnly
                className="w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
              />
            </div>
            <div data-testid="end-time-field-container">
              <label htmlFor="endTime" data-testid="end-time-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.end_time_label')}
              </label>
              <input
                id="endTime"
                data-testid="end-time-input"
                type="text"
                value={formatTime(computedEndTime())}
                readOnly
                className="w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
              />
            </div>
            {/* <div data-testid="communication-section" className="sm:col-span-2 lg:col-span-3">
              <div data-testid="communication-section-divider" className="border border-gray-200 h-[1px] my-10"></div>
              <h4 data-testid="communication-section-title" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-4">
                {t('patientappointmentmodal.communication_section')}
              </h4>
            </div> */}
            <div data-testid="communication-field-container" className="sm:col-span-2 lg:col-span-3">
              <label htmlFor="communication" data-testid="communication-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('patientappointmentmodal.communication_label')}
              </label>
              <textarea
                id="communication"
                data-testid="communication-input"
                value={communication}
                onChange={handleCommunicationChange}
                maxLength={500}
                className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300 ${communicationError
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-300'
                  }`}
                rows="4"
                disabled={appointmentLoading}
              />
              {communicationError && (
                <p className="mt-2 text-xs text-red-500 font-medium">{communicationError}</p>
              )}
            </div>
            {/* Payment step — placed last (after Communication), full width. In-modal, no stacked popup. */}
            {!isRescheduling && (
              <div className="sm:col-span-2 lg:col-span-3">
                <PaymentMethodSelect
                  doctorId={selectedDoctor?.doctorId}
                  value={payment}
                  onChange={setPayment}
                  summary={[
                    { label: t('patientappointmentmodal.price_label', 'Price'), value: formatPrice(price ?? 0, selectedDoctor?.country) },
                    { label: t('patientappointmentmodal.duration_label', 'Duration'), value: duration ? formatDuration(duration) : '—' },
                    { label: t('patientappointmentmodal.mode_label', 'Mode'), value: mode === 'visio' ? t('patientappointmentmodal.visio', 'Visio') : t('patientappointmentmodal.onsite', 'On-Site') },
                    { label: t('patientappointmentmodal.start_time_label', 'Start time'), value: formatTime(selectedSlot?.start) || '—' },
                    { label: t('patientappointmentmodal.location_label', 'Location'), value: location || '—' },
                  ]}
                />
              </div>
            )}
          </form>
        </div>

        {/* Footer Section - Sticky */}
        <div data-testid="form-actions-container" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
          <button
            data-testid="cancel-button"
            id="btn-cancel"
            type="button"
            onClick={handleCloseModal}
            className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
            disabled={appointmentLoading}
          >
            {t('patientappointmentmodal.cancel_button', 'Cancel')}
          </button>
          <button
            data-testid="submit-button"
            id="btn-submit"
            type="button"
            onClick={handleSaveAppointment}
            className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2"
            disabled={appointmentLoading}
          >
            {appointmentLoading && (
              <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
            )}
            {appointmentLoading
              ? t('patientappointmentmodal.loading_button')
              : isRescheduling
                ? t('patientappointmentmodal.reschedule_button')
                : t('patientappointmentmodal.create_button')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatientAppointmentModal;